import { OrderedMap } from 'immutable';

import {
  type AnyMenuItem,
  type ConstantMenuItem,
  type LocationList,
  type MatchMenuItem,
  MENU_ITEM_TYPE,
  type NavigationLocationProps,
  type NavigationLocationState,
  type SportMenuItemNative,
  type StageMenuItem,
  type TournamentMenuItem,
  type UnionMenuItem,
} from './types';
import type { DataLayerItem } from 'app/packages/analytics/types';

import {
  NAMESPACE_LANDING_MENU,
  NAMESPACE_LIVE,
  NAMESPACE_PREMATCH,
} from 'app/constants/Context';
import { type DictionaryData, isUseTranslationData, tFunction } from 'app/lang';
import type {
  RecordOfMatchEntity,
  RecordOfObjectsState,
  RecordOfSportEntity,
  RecordOfStageEntity,
  RecordOfTournamentEntity,
  RecordOfUnionEntity,
} from 'app/lib/betting/betting-entities';
import { createImmutableObjects, SlotType } from 'app/lib/betting/betting-entities';
import { ROUTE_PATH } from 'app/lib/routing/constants';

// --

const LOCATION_SCHEMA = OrderedMap([
  ['sports', 'sportId'],
  ['unions', 'tournamentUnionId'],
  ['tournaments', 'tournamentId'],
  ['stages', 'tournamentStageId'],
  ['matches', 'matchId'],
]);

export const getPrefixByNamespace = (namespace: string): string => {
  switch (namespace) {
    case NAMESPACE_LIVE:
    case NAMESPACE_PREMATCH:
      return `/${namespace}`;

    case NAMESPACE_LANDING_MENU:
      return `/${NAMESPACE_PREMATCH}`;

    default:
      throw new Error(`there is no pathname for "${namespace}" namespace`);
  }
};

export const getPrefixBySlotType = (slotType: number): string => {
  switch (slotType) {
    case SlotType.LIVE:
      return getPrefixByNamespace(NAMESPACE_LIVE);

    case SlotType.PREMATCH:
      return getPrefixByNamespace(NAMESPACE_PREMATCH);

    default:
      throw new Error(`unrecognized slot type ${slotType}`);
  }
};

/**
 * Compose betting entity path depending on the given location props.
 */
export const composeEntityPath = (location: NavigationLocationProps, prefix: string): string => {
  return LOCATION_SCHEMA.reduce((path, idField, nodeName) => {
    if (!location[idField as keyof NavigationLocationProps]) {
      return path;
    }

    return `${path}/${nodeName}/${location[idField as keyof NavigationLocationProps]}`;
  }, prefix);
};

/**
 * Compose sport entity path.
 */
export const composeSportPath = (
  { sportId }: NavigationLocationProps,
  prefix: string,
): string => {
  return composeEntityPath({ sportId }, prefix);
};

/**
 * Compose tournament union entity path.
 */
export const composeUnionPath = (
  { sportId, tournamentUnionId }: NavigationLocationProps,
  prefix: string,
): string => {
  return composeEntityPath({ sportId, tournamentUnionId }, prefix);
};

/**
 * Compose tournament entity path.
 */
export const composeTournamentPath = (
  { sportId, tournamentUnionId, tournamentId }: NavigationLocationProps,
  prefix: string,
): string => {
  return composeEntityPath({ sportId, tournamentUnionId, tournamentId }, prefix);
};

/**
 * Compose tournament stage entity path.
 */
export const composeStagePath = (
  {
    sportId,
    tournamentUnionId,
    tournamentId,
    tournamentStageId,
  }: NavigationLocationProps,
  prefix: string,
): string => {
  return composeEntityPath({ sportId, tournamentUnionId, tournamentId, tournamentStageId }, prefix);
};

/**
 * Compose match center path.
 */
export const composeMatchCenterPath = (
  {
    sportId,
    tournamentUnionId,
    tournamentId,
    tournamentStageId,
    matchId,
  }: NavigationLocationProps,
  prefix: string,
): string => {
  return composeEntityPath(
    { sportId, tournamentUnionId, tournamentId, tournamentStageId, matchId },
    prefix,
  );
};

// --

const LOCATION_PATH_PARSER =
  // eslint-disable-next-line max-len
  /^\/\w+(?:\/([\w-]+))?(?:\/sports\/(\d+))?(?:\/unions\/(\d+))?(?:\/tournaments\/(\d+))?(?:\/stages\/(\d+))?(?:\/matches\/(\d+))?\/?$/;

/**
 * Parse router location path.
 */
export const parseLocationPath = (path: string): NavigationLocationState => {
  const parsed = LOCATION_PATH_PARSER.exec(path);

  if (!parsed) {
    return {
      path,
      list: null,
      sportId: null,
      tournamentUnionId: null,
      tournamentId: null,
      tournamentStageId: null,
      matchId: null,
    };
  }

  return {
    path,
    list: (parsed[1] as LocationList) || null,
    sportId: parsed[2] ? parseInt(parsed[2]) : null,
    tournamentUnionId: parsed[3] ? parseInt(parsed[3]) : null,
    tournamentId: parsed[4] ? parseInt(parsed[4]) : null,
    tournamentStageId: parsed[5] ? parseInt(parsed[5]) : null,
    matchId: parsed[6] ? parseInt(parsed[6]) : null,
  };
};

// --

type ConstantMenuItemProps = (
  Omit<ConstantMenuItem, ('type' | 'shortcut' | 'active')> & {
  shortcut?: boolean;
});

export const createConstantMenuItem = ({
  key,
  shortcut,
  label,
  caption,
  title,
  location,
  activeOn,
  disabled,
  authOnly,
  ff,
  dataGaValues,
}: ConstantMenuItemProps,
): ConstantMenuItem => ({
  key,
  type: MENU_ITEM_TYPE.CONSTANT,
  shortcut: shortcut || false,
  label,
  caption,
  title,
  location,
  activeOn,
  active: false,
  disabled,
  authOnly,
  ff,
  dataGaValues,
});


type SportMenuItemProps = {
  sport: RecordOfSportEntity;
  shortcut?: boolean;
  prefix: string;
  dataGaValues?: DataLayerItem;
};

export const createSportMenuItem = (
  { sport, shortcut, prefix, dataGaValues }: SportMenuItemProps,
): SportMenuItemNative => ({
  key: `sport-${sport.id}`,
  type: MENU_ITEM_TYPE.SPORT,
  shortcut: shortcut || false,
  priority: sport.priority,
  label: sport.name,
  title: sport.name,
  location: composeSportPath({ sportId: sport.id }, prefix),
  sportId: sport.id,
  entity: {
    ...sport.toObject(),
    matchIds: sport.get('matchIds'),
  },
  active: false,
  dataGaValues,
});


type UnionMenuItemProps = {
  union: RecordOfUnionEntity;
  shortcut?: boolean;
  prefix: string;
  caption?: string;
};

export const createUnionMenuItem = (
  { union, shortcut, prefix, caption }: UnionMenuItemProps,
): UnionMenuItem => ({
  key: `union-${union.id}`,
  type: MENU_ITEM_TYPE.UNION,
  shortcut: shortcut || false,
  priority: union.priority,
  label: union.name,
  caption,
  title: union.name,
  location: composeUnionPath({
    sportId: union.sportId,
    tournamentUnionId: union.id,
  }, prefix),
  unionId: union.id,
  entity: {
    ...union.toObject(),
    matchIds: union.get('matchIds'),
  },
  active: false,
});


type TournamentMenuItemProps = {
  tournament: RecordOfTournamentEntity;
  shortcut?: boolean;
  location?: string;
  prefix?: string;
  caption?: string;
};

export const createTournamentMenuItem = (
  { tournament, shortcut, location, prefix, caption }: TournamentMenuItemProps,
): TournamentMenuItem => {
  if (!location) {
    if (!prefix) {
      throw new Error('Unable to compose tournament path: missing `prefix`');
    }

    location = composeTournamentPath({
      sportId: tournament.sportId,
      tournamentUnionId: tournament.tournamentUnionId,
      tournamentId: tournament.id,
    }, prefix);
  }

  return {
    key: `tournament-${tournament.id}`,
    type: MENU_ITEM_TYPE.TOURNAMENT,
    shortcut: shortcut || false,
    priority: tournament.priority,
    label: tournament.name,
    caption,
    title: tournament.name,
    location,
    tournamentId: tournament.id,
    entity: {
      ...tournament.toObject(),
      matchIds: tournament.get('matchIds'),
    },
    active: false,
  };
};


type StageMenuItemProps = {
  stage: RecordOfStageEntity;
  shortcut?: boolean;
  prefix: string;
  caption?: string;
};

export const createStageMenuItem = (
  { stage, shortcut, prefix, caption }: StageMenuItemProps,
): StageMenuItem => ({
  key: `stage-${stage.id}`,
  type: MENU_ITEM_TYPE.STAGE,
  shortcut: shortcut || false,
  priority: stage.priority,
  label: stage.name,
  caption,
  title: stage.name,
  location: composeStagePath({
    sportId: stage.sportId,
    tournamentUnionId: stage.tournamentUnionId,
    tournamentId: stage.tournamentId,
    tournamentStageId: stage.id,
  }, prefix),
  stageId: stage.id,
  entity: {
    ...stage.toObject(),
    matchIds: stage.get('matchIds'),
  },
  active: false,
});

// ---

type MatchMenuItemProps = {
  match: RecordOfMatchEntity;
  objects: RecordOfObjectsState['byId'];
  shortcut?: boolean;
  location?: string;
  prefix?: string;
};

export const createMatchMenuItem = (
  { match, shortcut, location, prefix, objects }: MatchMenuItemProps,
): MatchMenuItem => {
  if (!location) {
    if (!prefix) {
      throw new Error('Unable to compose match center path: missing `prefix`');
    }

    location = composeMatchCenterPath({
      sportId: match.sportId,
      tournamentUnionId: match.tournamentUnionId,
      tournamentId: match.tournamentId,
      tournamentStageId: match.tournamentStageId,
      matchId: match.id,
    }, prefix);
  }

  const matchObjects = createImmutableObjects(match.objects, objects);

  return {
    key: `match-${match.id}`,
    type: MENU_ITEM_TYPE.MATCH,
    shortcut: shortcut || false,
    label: match.name,
    title: match.name,
    matchObjects,
    location,
    matchId: match.id,
    entity: match,
    active: false,
  };
};

// --

const isMenuItemActive = (
  item: AnyMenuItem,
  location: NavigationLocationState,
): boolean => {
  if (location.path.includes('results')) {
    return false;
  }

  if (item.shortcut) {
    return false;
  }

  switch (item.type) {
    case MENU_ITEM_TYPE.CONSTANT:
      if (item.activeOn === 'betting') {
        return location.path.startsWith(ROUTE_PATH.LIVE)
          || location.path.startsWith(ROUTE_PATH.PREMATCH);
      }

      if (item.activeOn === 'exact-match') {
        return (item.location === location.path);
      }

      if (item.activeOn === 'prefix-match') {
        return location.path.startsWith(item.location);
      }

      return false;

    case MENU_ITEM_TYPE.SPORT:
      return (item.sportId === location.sportId && !location.list);

    case MENU_ITEM_TYPE.UNION:
      return (item.unionId === location.tournamentUnionId);

    case MENU_ITEM_TYPE.TOURNAMENT:
      return (item.tournamentId === location.tournamentId);

    case MENU_ITEM_TYPE.STAGE:
      return (item.stageId === location.tournamentStageId);

    case MENU_ITEM_TYPE.MATCH:
      return (item.matchId === location.matchId);

    default:
      console.warn('unrecognized menu entry %o', item); // >>>
      return false;
  }
};

export function detectActiveItem<T extends AnyMenuItem>(
  items: T[],
  location: NavigationLocationState,
): T[] {
  let activeItemIndex = -1;

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];

    if (isMenuItemActive(item, location)) {
      activeItemIndex = i;
      break;
    }
  }

  if (activeItemIndex === -1) {
    return items;
  }

  return items.map((item, i) => {
    if (i === activeItemIndex) {
      return { ...item, active: true };
    }
    return item;
  });
}

export const getDictionaryDataItems = (t:tFunction, data?: string | DictionaryData) => {
  if (isUseTranslationData(data)) {
    return t(data);
  }
  return data;
};

// --
